import React from 'react';
import { useFormContext } from 'react-hook-form';
import { HelpCircleIcon, ChevronDown } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { T2DomainEnum, T2DomainEnumLabels } from 'types/event.types';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Button } from '@epoch-ui/components/Button';
import { Popover, PopoverTrigger, PopoverContent } from '@epoch-ui/components/Popover/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from '@epoch-ui/components/Tooltip';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@epoch-ui/components/Form';

interface EventDomainFieldProps {
  name: string;
  className?: string;
  disabled?: boolean;
}

const Label = (): React.ReactNode => {
  return (
    <>
      Domain&nbsp;
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Domain selection help" className="tw-m-0  tw-p-0">
            <HelpCircleIcon size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="tw-max-w-80">
          <p>
            The event will appear on the event discover page for employees in the selected
            domain(s).
          </p>
        </TooltipContent>
      </Tooltip>
    </>
  );
};

const EventDomainField: React.FC<EventDomainFieldProps> = ({ name, className, disabled }) => {
  const { control } = useFormContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const [localSelection, setLocalSelection] = React.useState<T2DomainEnum[]>([]);

  const getDisplayValue = (selectedValues: T2DomainEnum[]): string => {
    if (!selectedValues || selectedValues.length === 0) {
      return 'Select';
    }
    return selectedValues.map((v) => T2DomainEnumLabels[v]).join(', ');
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }): React.ReactElement => {
        const currentValue: T2DomainEnum[] = Array.isArray(field.value) ? field.value : [];

        const handleOpenChange = (open: boolean): void => {
          if (open) {
            setLocalSelection(currentValue);
          }
          setIsOpen(open);
        };

        const handleCheckboxChange = (enumKey: T2DomainEnum, checked: boolean): void => {
          setLocalSelection((prev) =>
            checked ? [...prev, enumKey] : prev.filter((v) => v !== enumKey)
          );
        };

        const handleReset = (): void => {
          setLocalSelection([]);
        };

        const handleApply = (): void => {
          // Use empty array instead of undefined to ensure form properly tracks the change
          field.onChange(localSelection.length > 0 ? localSelection : []);
          setIsOpen(false);
        };

        return (
          <FormItem className={className}>
            <FormLabel>
              <Label />
            </FormLabel>
            <FormDescription>
              You won&apos;t be able to change this after the event is published. A reminder to not
              include 2K or VC unless you have approval to do so.
            </FormDescription>
            <FormControl>
              <Popover open={isOpen} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={disabled}
                    className={cn(
                      'tw-mt-2 tw-flex tw-h-12  tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border-[1.5px] tw-border-solid tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus-visible:tw-border-primary-blue disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
                      fieldState.error && 'tw-border-destructive-foreground'
                    )}
                  >
                    <span
                      className={cn(
                        'tw-truncate',
                        currentValue.length === 0 && 'tw-text-muted-foreground'
                      )}
                    >
                      {getDisplayValue(currentValue)}
                    </span>
                    <ChevronDown color="#555555" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="tw-w-[220px] tw-p-0">
                  <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
                    {Object.entries(T2DomainEnumLabels).map(([enumKey, label]) => (
                      <FormLabel
                        key={enumKey}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-font-normal"
                      >
                        <Checkbox
                          checked={localSelection.includes(enumKey as T2DomainEnum)}
                          onCheckedChange={(checked): void =>
                            handleCheckboxChange(enumKey as T2DomainEnum, checked === true)
                          }
                        />
                        <span className="tw-text-sm">{label}</span>
                      </FormLabel>
                    ))}
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-solid tw-border-light-grey tw-px-4 tw-py-3">
                    <Button type="button" variant="secondary" size="sm" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button type="button" size="sm" onClick={handleApply}>
                      Apply
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export { EventDomainField };
